import {
  AIRTIME_MANAGEMENT,
  CARRIER_MANAGEMENT,
  FILTER_FEATURE,
  PARTNER_MANAGEMENT,
  STORE_API,
} from "api/ApiPath";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { FilterUseCase } from "app/filters/domain/useCase/FilterUseCase";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { CapabilityEnum } from "utils/enums/CapabilityEnum";
import { DateFormat } from "utils/format/DateFormat";
import { paramsChecking } from "utils/helpersInTs/helpersInTs";
import { AirTimeRegistrationInjection } from "../../di/AirTimeRegistrationInjection";
import { AirTimeRegistrationDetailEntity } from "../entities/AirTimeRegistrationDetailEntity";
import { ExportPayload } from "../entities/AirTimeRegistrationPayload";
import {
  AirTimeRegistrationQuery,
  SearchContext,
  SearchFormValues,
} from "../entities/AirTimeRegistrationQuery";
import { AirTimeRegistrationRequestEntity } from "../entities/AirTimeRegistrationRequestEntity";
import { AirTimeRegistrationResponseEntity } from "../entities/AirTimeRegistrationResponseEntity";
import { IAirTimeRegistrationRepository } from "../repositories/IAirTimeRegistrationRepository";

@injectable()
export class AirTimeRegistrationUseCase {
  private airTimeRegistrationRepo: IAirTimeRegistrationRepository;
  private filterUseCase: FilterUseCase;

  constructor() {
    this.airTimeRegistrationRepo =
      AirTimeRegistrationInjection.getAirTimeRegistrationRepo();
    this.filterUseCase = FilterInjection.getUseCase();
  }

  public shouldUsePrivateTransactionList(): boolean {
    const cachedCapabilities = productCache?.getCache(
      CacheKeyEnum.capabilities
    );
    const hasPrivateTransactionList =
      Array.isArray(cachedCapabilities) &&
      cachedCapabilities.some(
        (item) =>
          (typeof item === "string" ? item : item?.key) ===
          CapabilityEnum.privateTransList
      );

    return hasPrivateTransactionList === true;
  }

  public shouldUsePrivateTransactionDetail(): boolean {
    const cachedCapabilities = productCache?.getCache(
      CacheKeyEnum.capabilities
    );
    const hasPrivateTransactionDetail =
      Array.isArray(cachedCapabilities) &&
      cachedCapabilities.some(
        (item) =>
          (typeof item === "string" ? item : item?.key) ===
          CapabilityEnum.privateTransDetail
      );

    return hasPrivateTransactionDetail === true;
  }

  private getListFromRepository(params: AirTimeRegistrationRequestEntity) {
    return this.airTimeRegistrationRepo.getList(
      params,
      this.shouldUsePrivateTransactionList()
    );
  }

  private prepareFormValues(
    rawFormValues: Record<string, unknown>
  ): SearchFormValues {
    return {
      status: paramsChecking(rawFormValues?.status, "select") as
        | number
        | string,
      code: paramsChecking(rawFormValues?.code, "input") as string,
      transRef: paramsChecking(rawFormValues?.transRef, "input") as string,
      type: paramsChecking(rawFormValues?.type, "select") as number | string,
      platform: paramsChecking(rawFormValues?.platform, "select") as string,
      partner: paramsChecking(rawFormValues?.partner, "select") as string,
      user: (rawFormValues?.user as string[]) || undefined,
      store: paramsChecking(rawFormValues?.store, "select") as string,
    };
  }

  private getDateRange(rawFormValues: Record<string, unknown>): {
    dateFrom: string;
    dateTo: string;
  } {
    const dateFrom =
      DateFormat.dateRendering(
        rawFormValues?.created as Array<Record<string, unknown>>,
        "from"
      ) ?? "";
    const dateTo =
      DateFormat.dateRendering(
        rawFormValues?.created as Array<Record<string, unknown>>,
        "to"
      ) ?? "";
    return { dateFrom, dateTo };
  }

  async getAirTimeRegistrationList(
    params: AirTimeRegistrationRequestEntity
  ): Promise<ListBaseApiResponseModel<AirTimeRegistrationResponseEntity>> {
    return await this.getListFromRepository(params);
  }

  async getAirTimeRegistrationListFromSearch(
    rawFormValues: Record<string, unknown>,
    context: SearchContext
  ): Promise<{
    list: ListBaseApiResponseModel<AirTimeRegistrationResponseEntity>;
    params: AirTimeRegistrationRequestEntity;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(rawFormValues);
    const params = AirTimeRegistrationQuery.buildSearchQuery(
      formValues,
      context,
      dateFrom,
      dateTo
    );
    const list = await this.getListFromRepository(params);
    return { list, params };
  }

  async getAirTimeRegistrationListWithTableChange(
    prevParams: AirTimeRegistrationRequestEntity,
    rawFormValues: Record<string, unknown>,
    page: number,
    limit: number
  ): Promise<{
    list: ListBaseApiResponseModel<AirTimeRegistrationResponseEntity>;
    params: AirTimeRegistrationRequestEntity;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(rawFormValues);
    const params = AirTimeRegistrationQuery.buildTableChangeQuery(
      prevParams,
      formValues,
      page,
      limit,
      dateFrom,
      dateTo
    );
    const list = await this.getListFromRepository(params);
    return { list, params };
  }

  async getAirTimeRegistrationListRedo(): Promise<{
    list: ListBaseApiResponseModel<AirTimeRegistrationResponseEntity>;
    params: AirTimeRegistrationRequestEntity;
  }> {
    const { fromDate, toDate } = DateFormat.getDateRange(0);
    const params = AirTimeRegistrationQuery.buildRedoQuery(
      fromDate ?? "",
      toDate ?? ""
    );
    const list = await this.getListFromRepository(params);
    return { list, params };
  }

  async getAirTimeRegistrationDetail(
    params: AirTimeRegistrationRequestEntity
  ): Promise<BaseApiResponseModel<AirTimeRegistrationDetailEntity>> {
    return await this.airTimeRegistrationRepo.getDetail(
      params,
      this.shouldUsePrivateTransactionDetail()
    );
  }

  async exportAirTimeRegistrationReport(
    params: AirTimeRegistrationRequestEntity
  ): Promise<BaseApiResponseModel<Blob>> {
    return await this.airTimeRegistrationRepo.exportReport(
      params,
      this.shouldUsePrivateTransactionList()
    );
  }

  async exportAirTimeRegistrationReportFromSearch(
    payload: ExportPayload
  ): Promise<BaseApiResponseModel<Blob>> {
    const formValues = this.prepareFormValues(payload.rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(payload.rawFormValues);
    const params = AirTimeRegistrationQuery.buildExportQuery(
      formValues,
      payload.context,
      dateFrom,
      dateTo
    );
    return await this.exportAirTimeRegistrationReport(params);
  }

  async getFilterList(
    params: BaseFilterListRequestModel,
    target: "carrier" | "partner" | "store" | "denomination"
  ): Promise<Array<BaseFilterListResponseModel>> {
    let path = "";
    switch (target) {
      case "carrier":
        path = CARRIER_MANAGEMENT.LIST;
        break;
      case "partner":
        path = PARTNER_MANAGEMENT.LIST;
        break;
      case "store":
        path = STORE_API.LIST;
        break;
      case "denomination":
        path = AIRTIME_MANAGEMENT.LIST;
        break;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
      isNeedFeature: true,
    });
  }

  async getUserForFilter(
    params: BaseUserRequestModel,
    target: "staff" | "userB2B"
  ): Promise<Array<BaseUserResponseModel>> {
    let path = "";
    switch (target) {
      case "staff":
        path = FILTER_FEATURE.STAFF_LIST;
        break;
      case "userB2B":
        path = FILTER_FEATURE.USER_LIST_B2B;
        break;
    }
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
    });
  }
}
